import { cloneDeep } from '@apollo/client/utilities';
import { EntityInput, forCreate, forDelete, forUpdate } from '../common/entity';
import {
  CustomField,
  CustomFieldEntityChildType,
  CustomFieldEntityParentType,
  CustomFieldEntitySubType,
  CustomFieldEntityType,
  CustomFieldType,
} from './customField';

export class CustomFieldInput extends CustomField implements EntityInput<CustomField> {
  forCreate() {
    return CreateCustomFieldInput.from(this, CreateCustomFieldInput);
  }

  forUpdate() {
    return UpdateCustomFieldInput.from(this, UpdateCustomFieldInput);
  }

  forDelete() {
    return DeleteCustomFieldInput.from(this, DeleteCustomFieldInput);
  }

  validate() {
    if (!this.name) return false;
    if ((this.type === CustomFieldType.listOfValues && !this.reference) || this.type === CustomFieldType.listOfTuples) {
      if (!this.values.length) return false;
    }

    return true;
  }

  hasChanges(original: CustomField | undefined) {
    if (!original) return true;
    if (this.values.sort().join(',') !== original.values.sort().join(',')) return true;
    if (this.name !== original.name) return true;
    if (this.mandatory !== original.mandatory) return true;
    if (this.generated !== original.generated) return true;
    if (this.index !== original.index) return true;
    if ((this.defaultValue || original.defaultValue) && this.defaultValue !== original.defaultValue) return true;
    if (this.hidden !== original.hidden) return true;
    if (this.onCreate !== original.onCreate) return true;
    if (this.parentId !== original.parentId) return true;
    if (this.reference !== original.reference) return true;
    if (this.manual !== original.manual) return true;
    if (this.displayMobile !== original.displayMobile) return true;

    return false;
  }

  withName(name: string) {
    this.name = name;
    return cloneDeep(this);
  }

  withType(type: CustomFieldType) {
    this.type = type;
    this.defaultValue = undefined;
    if (!this.validReference()) this.reference = undefined;
    return cloneDeep(this);
  }

  withReference(reference?: CustomFieldEntityType) {
    this.reference = reference;
    return cloneDeep(this);
  }

  withEntityType(entityType: CustomFieldEntityType) {
    this.entityType = entityType;
    if (this.entitySubtype && !this.validEntitySubtypes().includes(this.entitySubtype)) {
      this.entitySubtype = this.validEntitySubtypes()[0];
    }

    if (this.type && !this.validTypes().includes(this.type)) {
      this.type = this.validTypes()[0];
    }

    if (this.entityParentType && !this.validEntityParentTypes().includes(this.entityParentType)) {
      this.entityParentType = this.validEntityParentTypes().at(0);
    }

    this.defaultValue = undefined;

    return cloneDeep(this);
  }

  withEntitySubtype(entitySubtype?: CustomFieldEntitySubType | null) {
    this.entitySubtype = entitySubtype || undefined;
    return cloneDeep(this);
  }

  withEntityParentType(entityParentType?: CustomFieldEntityParentType) {
    this.entityParentType = entityParentType;
    return cloneDeep(this);
  }

  withEntityChildType(entityChildType?: CustomFieldEntityChildType) {
    this.entityChildType = entityChildType;
    return cloneDeep(this);
  }

  withMandatory(mandatory: boolean) {
    this.mandatory = mandatory;
    return cloneDeep(this);
  }

  withGenerated(value: boolean) {
    this.generated = value;
    return cloneDeep(this);
  }

  withHidden(value: boolean) {
    this.hidden = value;
    return cloneDeep(this);
  }

  withManual(value: boolean) {
    this.manual = value;
    return cloneDeep(this);
  }

  withOnCreate(value: boolean) {
    this.onCreate = value;
    return cloneDeep(this);
  }

  withParentId(parentId?: string | null) {
    this.parentId = parentId || undefined;
    return cloneDeep(this);
  }

  withDefaultValue(value?: string) {
    this.defaultValue = value;
    return cloneDeep(this);
  }

  withDateDefaultValue(number: number) {
    this.defaultValue = number.toString();
    return cloneDeep(this);
  }

  withIndex(index: number) {
    this.index = index;
    return cloneDeep(this);
  }

  withValues(values: string[]) {
    this.values = values;
    return cloneDeep(this);
  }

  hasValue(value: string) {
    return this.values.includes(value);
  }

  addValue(value: string) {
    if (this.hasValue(value)) return;
    this.values = [...this.values, value];
    return cloneDeep(this);
  }

  removeValue(value: string) {
    if (this.defaultValue === value) this.defaultValue = undefined;
    this.values = this.values.filter(v => v !== value);
    return cloneDeep(this);
  }

  withDisplayMobile(displayMobile: boolean) {
    this.displayMobile = displayMobile;
    return cloneDeep(this);
  }
}

export class CreateCustomFieldInput extends forCreate(CustomField) {}

export class UpdateCustomFieldInput extends forUpdate(CustomField) {}

export class DeleteCustomFieldInput extends forDelete(CustomField) {}
